import React, { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { IOfferVisitor } from 'api/interfaces/offers/IOfferVisit';
import translations from 'services/translations/translations';
import { addMessage } from 'store/messages/actions';

/**
 * The other users on the offer now, on its page or calculating its positions. The maker of
 * the offer gets a notice when someone new comes.
 */
const OfferVisitors = ({
  isMaker,
  others,
}: {
  isMaker: boolean;
  others: IOfferVisitor[];
}): JSX.Element | null => {
  const dispatch = useDispatch();
  const { t } = useTranslation([translations.offers]);
  // Who the maker has been told about, by user and position
  const notified = useRef(new Set<string>());

  useEffect(() => {
    if (!isMaker) {
      return;
    }

    others.forEach((other) => {
      const key = `${other.userId}-${other.offerProductId ?? ''}`;
      if (notified.current.has(key)) {
        return;
      }

      notified.current.add(key);
      dispatch(
        addMessage(
          other.position === null
            ? t(`${translations.offers}:concurrency.Opened your offer`, {
                name: other.name,
              })
            : t(`${translations.offers}:concurrency.Calculating your offer`, {
                name: other.name,
                position: other.position,
              })
        )
      );
    });
  }, [isMaker, others]);

  if (others.length === 0) {
    return null;
  }

  return (
    <div className="offer-visitors" role="status">
      <span className="offer-visitors-title">
        {t(`${translations.offers}:concurrency.On the offer`)}
      </span>{' '}
      {others
        .map(
          (other) =>
            `${other.name} (${
              other.position === null
                ? t(`${translations.offers}:concurrency.Viewing`)
                : t(`${translations.offers}:concurrency.Calculating`, {
                    position: other.position,
                  })
            })`
        )
        .join(', ')}
    </div>
  );
};

export default OfferVisitors;
